import { Button, Icon, Typography } from '@smartgift/frontend-toolkit';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AvailableCharitiesModal,
  DonateToCharityModal,
  FeeBreakdownInfoModal
} from '@hero/components/modals';
import { useCreatePriceLabel, useGetCharity } from '@hero/hooks';
import { useShoppingCart } from '@hero/store/hooks';
import { formatCurrency, getFormattedPrice } from '@hero/utils';

import { ChooseFeatureTypeModal } from './ChooseFeatureTypeModal';
import * as S from './style';

function WidgetFooter({ products, onConfirmCart, components }) {
  const { t } = useTranslation();
  const { setOpened, updateProducts } = useShoppingCart();
  const { charityEnabled, isCharityExist, anyCharityItemExists } = useGetCharity();

  const hasCharityItem = anyCharityItemExists(products);

  const [isFeeBreakdownModalOpen, setIsFeeBreakdownModalOpen] = useState(false);
  const [chooseFeatureModal, setChooseFeatureModal] = useState(false);
  const [showCharityInfoModal, setShowCharityInfoModal] = useState(false);
  const [showSelectCharitiesModal, setShowSelectCharitiesModal] = useState(false);

  const { isCAActMustBeApplied } = useCreatePriceLabel({});

  const selectedSkus = useMemo(() => {
    return products.reduce((r, p) => r.concat(p.skus), []);
  }, [products]);

  const maxPrice = isCAActMustBeApplied
    ? maxBy(selectedSkus, 'priceBreakdown.display')?.priceBreakdown?.display
    : maxBy(selectedSkus, 'price')?.price;
  const minPrice = isCAActMustBeApplied
    ? minBy(selectedSkus, 'priceBreakdown.display')?.priceBreakdown?.display
    : minBy(selectedSkus, 'price')?.price;

  if (components?.WidgetFooter) {
    return (
      <components.WidgetFooter
        products={products}
        maxPrice={maxPrice}
        minPrice={minPrice}
        isCAActMustBeApplied={isCAActMustBeApplied}
        onConfirmCart={onConfirmCart}
      />
    );
  }

  return (
    <S.WidgetFooter>
      <S.WidgetFooterSubtotal>
        <Typography variant="body2">
          {t('priceRange')}:
          <Typography variant="body1Bold" as="span">
            {products.length > 0
              ? getFormattedPrice({ min: minPrice, max: maxPrice })
              : formatCurrency(0)}
          </Typography>
        </Typography>
        {isCAActMustBeApplied && (
          <S.WidgetFooterFeeBreakdownModalButton
            variant="link"
            onClick={() => setIsFeeBreakdownModalOpen(true)}
          >
            {t('feeBreakdownModalOpenButtonText')}
          </S.WidgetFooterFeeBreakdownModalButton>
        )}
      </S.WidgetFooterSubtotal>
      <S.FooterActionButtons>
        <Button
          uppercase={false}
          endIcon={<Icon name="next" />}
          onClick={() => {
            if (onConfirmCart) {
              return onConfirmCart();
            }
            if (charityEnabled && !hasCharityItem && isCharityExist) setShowCharityInfoModal(true);
            else setChooseFeatureModal(true);
          }}
          size="md"
          block
        >
          {t('chooseGiftingMethod')}
        </Button>
        <Button
          uppercase={false}
          variant="text"
          onClick={() => setOpened(false)}
          color="secondary"
          size="md"
          block
        >
          {t('continueBrowsing')}
        </Button>
      </S.FooterActionButtons>
      <ChooseFeatureTypeModal
        isOpen={chooseFeatureModal}
        onClose={() => {
          setChooseFeatureModal(false);
        }}
      />
      {isFeeBreakdownModalOpen && (
        <FeeBreakdownInfoModal
          isOpen={isFeeBreakdownModalOpen}
          onRequestClose={() => setIsFeeBreakdownModalOpen(false)}
        />
      )}
      {showCharityInfoModal && (
        <DonateToCharityModal
          isOpen={showCharityInfoModal}
          setIsOpen={setShowCharityInfoModal}
          primaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              setShowSelectCharitiesModal(true);
            },
            children: t('yes')
          }}
          secondaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              setChooseFeatureModal(true);
            },
            children: t('skip')
          }}
          descriptionTextKey="donateToCharityDesc2"
          titleKey="donateToCharityTitle2"
        />
      )}
      {showSelectCharitiesModal && (
        <AvailableCharitiesModal
          isOpen={showSelectCharitiesModal}
          setIsOpen={setShowSelectCharitiesModal}
          setSelectedCharities={selectedCharities =>
            updateProducts([...products, ...selectedCharities])
          }
        />
      )}
    </S.WidgetFooter>
  );
}

export { WidgetFooter };
